import { useState, useEffect, useCallback, useRef } from 'react';
import useToken from 'hooks/useToken';

const PAGE_SIZE = 5;

export const useLongestActiveTickets = (idUsuarioAsignado = null) => {
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useToken();
  const isFetchingRef = useRef(false);

  const totalPages = Math.max(1, Math.ceil(totalRegistros / PAGE_SIZE));

  const fetchTickets = useCallback(async (targetPage = 1) => {
    if (!token || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const queryParams = [`pageNumber=${targetPage}`, `pageSize=${PAGE_SIZE}`];
      if (idUsuarioAsignado && idUsuarioAsignado !== 'todos' && idUsuarioAsignado !== 'all') {
        queryParams.push(`idUsuarioAsignado=${idUsuarioAsignado}`);
      }
      const url = `/api/SupportEngine/GetDashboardLongestActiveTickets?${queryParams.join('&')}`;

      const resp = await fetch(url, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (resp.ok) {
        const json = await resp.json();
        const data = json.data || (Array.isArray(json) ? json : []);
        const list = Array.isArray(data) ? data : [];
        const total = list[0]?.iTotalRegistros ?? list.length;
        setTotalRegistros(total);
        setTickets(list);
        setPage(targetPage);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [token, idUsuarioAsignado]);

  useEffect(() => {
    setPage(1);
    fetchTickets(1);
  }, [fetchTickets]);

  const goToNextPage = useCallback(() => {
    if (page < totalPages && !isLoading) {
      fetchTickets(page + 1);
    }
  }, [page, totalPages, isLoading, fetchTickets]);

  const goToPrevPage = useCallback(() => {
    if (page > 1 && !isLoading) {
      fetchTickets(page - 1);
    }
  }, [page, isLoading, fetchTickets]);

  const goToPage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !isLoading) {
      fetchTickets(newPage);
    }
  }, [totalPages, isLoading, fetchTickets]);

  return {
    tickets,
    page,
    totalPages,
    totalRegistros,
    isLoading,
    error,
    goToNextPage,
    goToPrevPage,
    goToPage,
    reloadTickets: () => fetchTickets(page)
  };
};
